import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DurationPicker } from "@/components/DurationPicker";
import { TimeField } from "@/components/TimeField";
import api, { apiErr } from "@/lib/api";
import { fmtDuration } from "@/lib/agenda";
import { toast } from "sonner";

export const EditSessionDialog = ({ open, onOpenChange, session, dogNames, ownerName, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !session) return;
    setForm({
      date: session.date || "",
      start_time: session.start_time || "",
      duration_minutes: session.duration_minutes || 60,
      location: session.location || "",
      notes: session.notes || "",
    });
  }, [open, session]);

  if (!session) return null;

  const dogs = (dogNames && dogNames.length ? dogNames : session.dog_names) || [];
  const owner = ownerName || session.owner_name || "";

  const dirty =
    form.date !== (session.date || "") ||
    (form.start_time || "") !== (session.start_time || "") ||
    Number(form.duration_minutes) !== Number(session.duration_minutes || 0) ||
    (form.location || "") !== (session.location || "") ||
    (form.notes || "") !== (session.notes || "");

  const submit = async () => {
    if (!form.date) return toast.error("La data è obbligatoria.");
    if (!dirty) { onOpenChange(false); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/sessions/${session.id}/schedule`, {
        date: form.date,
        start_time: form.start_time || "",
        duration_minutes: Number(form.duration_minutes),
        location: form.location || "",
        notes: form.notes || "",
      });
      if (r.data?.calendar_warning) toast.warning(r.data.calendar_warning);
      else toast.success("Prestazione aggiornata");
      onSaved?.(r.data?.session);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[92vh] overflow-y-auto wg-scroll" data-testid="edit-session-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Modifica prestazione</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm">
              <span className="block">Cane: <b className="text-foreground">{dogs.join(", ") || "—"}</b></span>
              {owner && <span className="block">Proprietario: <b className="text-foreground">{owner}</b></span>}
              <span className="block">Servizio: <b className="text-foreground">{session.service_type}</b></span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Data</Label>
              <Input type="date" data-testid="edit-sess-date" value={form.date || ""} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>Ora inizio</Label>
              <TimeField value={form.start_time || ""} onChange={(v) => setForm((f) => ({ ...f, start_time: v }))} testid="edit-sess-time" />
            </div>
            <div className="space-y-1">
              <Label>Durata</Label>
              {session.engine === "v2" ? <p className="text-sm" data-testid="edit-sess-v2-duration">{fmtDuration(form.duration_minutes) || "—"} · Per cambiare la durata dei servizi, apri la modifica completa in Prestazioni.</p> :
                <DurationPicker value={form.duration_minutes} onChange={(m) => setForm((f) => ({ ...f, duration_minutes: m }))} testid="edit-sess-duration" />}
            </div>
            <div className="space-y-1">
              <Label>Luogo</Label>
              <Input data-testid="edit-sess-location" value={form.location || ""} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Note</Label>
            <Textarea data-testid="edit-sess-notes" value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="edit-sess-cancel" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="edit-sess-save" onClick={submit} disabled={saving || !dirty}>{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
